/**
 * lib/billing/index.ts — Billing Engine Factory
 * ===============================================
 * Resolves the correct billing engine for a given state code and
 * calls it with the tariff config already injected.
 *
 * TO ADD A NEW STATE:
 *  1. Create src/lib/billing/engines/<state-code>.ts
 *  2. Register it in BILLING_ENGINES below (one line)
 *  Done. No other changes needed.
 */

import type { TelemetryRow } from '@/types';
import type { StateCode, StateTariff } from '@/tariffs';
import { getTariff } from '@/tariffs';
import type { BillBreakdown } from './types';

import { buildWestBengalBreakdown } from './engines/west-bengal';
import { buildTelanganaBreakdown }  from './engines/telangana';
// import { buildMadhyaPradeshBreakdown } from './engines/madhya-pradesh';
// import { buildMaharashtraBreakdown } from './engines/maharashtra';

// ─── Engine type ──────────────────────────────────────────────────────────────

export type BillingEngineFn = (
  rows:                       TelemetryRow[],
  tariff:                     StateTariff,
  daysPassed?:                number,
  daysTotal?:                 number,
  contractDemandKvaOverride?: number,
) => BillBreakdown;

// ─── Registry ─────────────────────────────────────────────────────────────────

const BILLING_ENGINES: Partial<Record<StateCode, BillingEngineFn>> = {
  'west-bengal': buildWestBengalBreakdown,
  'telangana':   buildTelanganaBreakdown,
  // 'madhya-pradesh': buildMadhyaPradeshBreakdown,
  // 'maharashtra': buildMaharashtraBreakdown,
};

// ─── Factory functions ────────────────────────────────────────────────────────

/**
 * Returns the raw engine function for a state.
 * Throws if no engine is registered for the state.
 */
export function getBillingEngine(stateCode: StateCode): BillingEngineFn {
  const engine = BILLING_ENGINES[stateCode];
  if (!engine) {
    throw new Error(
      `[Cortex] No billing engine registered for state "${stateCode}". ` +
      `Create src/lib/billing/engines/${stateCode}.ts and register it in src/lib/billing/index.ts.`
    );
  }
  return engine;
}

/**
 * Convenience: build a bill breakdown for any state in one call.
 * Resolves tariff + engine automatically from stateCode.
 *
 * @example
 * const bill = buildBreakdown('telangana', rows, 15, 31, 2501);
 */
export function buildBreakdown(
  stateCode:                  StateCode,
  rows:                       TelemetryRow[],
  daysPassed?:                number,
  daysTotal?:                 number,
  contractDemandKvaOverride?: number,
): BillBreakdown {
  const tariff = getTariff(stateCode);
  const engine = getBillingEngine(stateCode);
  return engine(rows, tariff, daysPassed, daysTotal, contractDemandKvaOverride);
}

// ─── Re-exports ───────────────────────────────────────────────────────────────

export type { BillBreakdown, BillPeriodBreakdown } from './types';
